import React from 'react'
import dayjs from 'dayjs'
import api from '@/services/api'
import CloseIcon from '@mui/icons-material/Close'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import BadgeIcon from '@mui/icons-material/Badge'
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'
import InfoIcon from '@mui/icons-material/Info'
import VerifiedIcon from '@mui/icons-material/Verified'
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt'
import BlockIcon from '@mui/icons-material/Block'
import VisibilityIcon from '@mui/icons-material/Visibility'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import PersonIcon from '@mui/icons-material/Person'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Grid,
  Chip,
  Card,
  CardContent,
  Typography,
  Alert,
  Button,
  Box,
  DialogActions,
  Divider,
  CircularProgress,
  Pagination,
} from '@mui/material'
import NoAccountsIcon from '@mui/icons-material/NoAccounts'

const formatDateMMDDYY = (date: string | Date | null | undefined) => {
  if (!date) return '--'
  const dayjsDate = dayjs(date)
  if (!dayjsDate.isValid()) return '--'
  return dayjsDate.format('MM/DD/YYYY')
}

export interface AgencyDetailsDialogProps {
  open: boolean
  onClose: () => void
  provider: any
  actions?: React.ReactNode
  onOpenProviderList?: (provider: any) => void
  onOpenUserList?: (provider: any) => void
}

const AgencyDetailsDialog: React.FC<AgencyDetailsDialogProps> = ({
  open,
  onClose,
  provider,
  actions,
  onOpenProviderList,
  onOpenUserList,
}) => {
  const [services, setServices] = React.useState<any[]>([])
  const [loadingServices, setLoadingServices] = React.useState(false)
  const [servicesDialogOpen, setServicesDialogOpen] = React.useState(false)
  const [servicesPage, setServicesPage] = React.useState(1)
  const [totalServices, setTotalServices] = React.useState(0)
  const servicesLimit = 2

  const fetchServices = async (page: number) => {
    setLoadingServices(true)
    try {
      const response = await api.get(`/services?page=${page}&limit=${servicesLimit}&agencyId=${provider._id}`)
      setServices(response.data?.services || response.data || [])
      setTotalServices(response.data?.totalRecords || 0)
    } catch (err) {
      console.error('Failed to fetch services:', err)
    } finally {
      setLoadingServices(false)
    }
  }

  const handleOpenServicesDialog = async () => {
    setServicesDialogOpen(true)
    setServicesPage(1)
    await fetchServices(1)
  }

  const handleServicesPageChange = async (event: React.ChangeEvent<unknown>, value: number) => {
    setServicesPage(value)
    await fetchServices(value)
  }

  const handleCloseServicesDialog = () => {
    setServicesDialogOpen(false)
    setServices([])
    setServicesPage(1)
    setTotalServices(0)
  }

  const [bankDetails, setBankDetails] = React.useState<any>(null)
  const [loadingBank, setLoadingBank] = React.useState(false)
  const [bankDialogOpen, setBankDialogOpen] = React.useState(false)

  const handleOpenBankDialog = async () => {
    setBankDialogOpen(true)
    setLoadingBank(true)
    try {
      const response = await api.get(`/therapist-subscriptions/connected-account/${provider._id}`)
      const accountData = response.data?.data?.[0] || response.data?.[0] || response.data || null
      setBankDetails(accountData)
    } catch (err) {
      console.error('Failed to fetch bank details:', err)
    } finally {
      setLoadingBank(false)
    }
  }

  const handleCloseBankDialog = () => {
    setBankDialogOpen(false)
    setBankDetails(null)
  }

  if (!provider) return null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          pt: 3,
          px: 3,
          background: 'linear-gradient(135deg, #5BC0BE 0%, #4a9d9b 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PersonOutlineIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, letterSpacing: 0.5 }}
            >
              Agency Details
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.2)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{ pt: '1rem !important', pb: 2, px: 3, bgcolor: '#f8f9fa' }}
      >
        <Grid container spacing={3}>
          {/* Profile Section */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                mb: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'white',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 3,
                    pb: 2,
                    borderBottom: '2px solid',
                    borderColor: '#5BC0BE',
                  }}
                >
                  <PersonOutlineIcon sx={{ color: '#5BC0BE', fontSize: 28 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2c3e50',
                      letterSpacing: 0.3,
                    }}
                  >
                    Agency Information
                  </Typography>

                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Agency Name
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: '#2c3e50',
                        fontSize: '1rem',
                      }}
                    >
                      {provider?.agencyName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Agency Website
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider?.website || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Agency Email
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider?.companyEmail || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Agency Phone
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.companyPhone || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Agency States
                    </Typography>



                    {provider.companyStates.map(state => (
                      <Chip
                        label={state}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          height: 28,
                          '& .MuiChip-label': {
                            px: 1.5,
                          },
                          mx: 0.5
                        }}
                      />))}


                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Number of Provider
                    </Typography>
                    {(() => {
                      const count = Array.isArray(provider.activeProviderCount)
                        ? provider.activeProviderCount.length
                        : provider.noOfProvider ?? 0
                      return count > 0 && onOpenProviderList ? (
                        <Box
                          component="button"
                          type="button"
                          onClick={() => onOpenProviderList(provider)}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '50px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            bgcolor: '#e6f7f7',
                            color: '#3a9e9c',
                            border: '1px solid #b2e5e4',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: '#d0f0ef',
                              boxShadow: '0 2px 6px rgba(58, 158, 156, 0.2)',
                            },
                          }}
                        >
                          <span>{count}</span>
                          <span style={{ fontSize: '0.65rem', fontWeight: 400 }}>(View)</span>
                        </Box>
                      ) : (
                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#495057', fontSize: '0.95rem' }}>
                          {count}
                        </Typography>
                      )
                    })()}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Number of Non-Providers
                    </Typography>
                    {(() => {
                      const count = Array.isArray(provider.activeUserCount)
                        ? provider.activeUserCount.length
                        : provider.noOfUser ?? 0
                      return count > 0 && onOpenUserList ? (
                        <Box
                          component="button"
                          type="button"
                          onClick={() => onOpenUserList(provider)}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '50px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            bgcolor: '#e6f7f7',
                            color: '#3a9e9c',
                            border: '1px solid #b2e5e4',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: '#d0f0ef',
                              boxShadow: '0 2px 6px rgba(58, 158, 156, 0.2)',
                            },
                          }}
                        >
                          <span>{count}</span>
                          <span style={{ fontSize: '0.65rem', fontWeight: 400 }}>(View)</span>
                        </Box>
                      ) : (
                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#495057', fontSize: '0.95rem' }}>
                          {count}
                        </Typography>
                      )
                    })()}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Status
                    </Typography>
                    <Chip
                      label={provider.isActive ? 'Active' : 'Inactive'}
                      color={provider.isActive === true ? 'success' : 'error'}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        height: 28,
                        '& .MuiChip-label': {
                          px: 1.5,
                        },
                      }}
                    />
                  </Grid>
                  {/* <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      No of provider/user paid:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.noOfUser || '--'}
                    </Typography>
                  </Grid> */}

                  {provider.bio && (
                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6c757d',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          fontSize: '0.7rem',
                          mb: 0.5,
                          display: 'block',
                        }}
                      >
                        Bio
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.5,
                          color: '#495057',
                          lineHeight: 1.7,
                          bgcolor: '#f8f9fa',
                          p: 1.5,
                          borderRadius: 1,
                        }}
                      >
                        {provider.bio}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Address Section */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'white',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 3,
                    pb: 2,
                    borderBottom: '2px solid',
                    borderColor: '#5BC0BE',
                  }}
                >
                  <LocationOnIcon sx={{ color: '#5BC0BE', fontSize: 28 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2c3e50',
                      letterSpacing: 0.3,
                    }}
                  >
                    Address Information
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Address Line
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.addressLine1 || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Address Line
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.addressLine2 || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      City
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.city || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      State
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.state || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Zip Code
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.zipCode || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Country
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.country || '--'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Billing Information */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'white',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 3,
                    pb: 2,
                    borderBottom: '2px solid',
                    borderColor: '#5BC0BE',
                  }}
                >
                  <AccountBalanceIcon sx={{ color: '#5BC0BE', fontSize: 28 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2c3e50',
                      letterSpacing: 0.3,
                    }}
                  >
                    Billing Information
                  </Typography>
                </Box>
                <Grid container spacing={3}>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Agency Npi
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.companyNpi || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Billing NPI
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.billingNpi || '--'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      EIN
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.companyEin || '--'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Tax ID
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.taxId || '--'}
                    </Typography>
                  </Grid>

                  {provider.companyName && (
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6c757d',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          fontSize: '0.7rem',
                          mb: 0.5,
                          display: 'block',
                        }}
                      >
                        Company Name
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          color: '#495057',
                          fontSize: '0.95rem',
                        }}
                      >
                        {provider.companyName}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Billing Phone
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.billingPhone || '--'}
                    </Typography>
                  </Grid>
                  {provider.billingAddressLine && (
                    <>
                      <Grid item xs={12}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#6c757d',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            fontSize: '0.7rem',
                            mb: 0.5,
                            display: 'block',
                          }}
                        >
                          Billing Address
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            color: '#495057',
                            fontSize: '0.95rem',
                          }}
                        >
                          {provider.billingAddressLine}
                          {provider.billingCity && `, ${provider.billingCity}`}
                          {provider.billingState &&
                            `, ${provider.billingState}`}
                          {provider.billingZipCode &&
                            ` ${provider.billingZipCode}`}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Licenses Section */}
          {provider.licenses && provider.licenses.length > 0 && (
            <Grid item xs={12}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'white',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      mb: 3,
                      pb: 2,
                      borderBottom: '2px solid',
                      borderColor: '#5BC0BE',
                    }}
                  >
                    <BadgeIcon sx={{ color: '#5BC0BE', fontSize: 28 }} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#2c3e50',
                        letterSpacing: 0.3,
                      }}
                    >
                      License Information
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    {provider.licenses.map((license: any, index: number) => (
                      <Grid item xs={12} key={index}>
                        <Card
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: '#e9ecef',
                            bgcolor: '#f8f9fa',
                          }}
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#6c757d',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                  fontSize: '0.7rem',
                                  mb: 0.5,
                                  display: 'block',
                                }}
                              >
                                Specialty License Type
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 600,
                                  color: '#2c3e50',
                                  fontSize: '0.95rem',
                                }}
                              >
                                {typeof license.licenseType === 'object'
                                  ? license.licenseType?.name || license.licenseType?.task || '--'
                                  : license.licenseType || '--'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#6c757d',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                  fontSize: '0.7rem',
                                  mb: 0.5,
                                  display: 'block',
                                }}
                              >
                                License Number
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 600,
                                  color: '#2c3e50',
                                  fontSize: '0.95rem',
                                }}
                              >
                                {license.licenseNumber || '--'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#6c757d',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                  fontSize: '0.7rem',
                                  mb: 0.5,
                                  display: 'block',
                                }}
                              >
                                License State
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 500,
                                  color: '#495057',
                                  fontSize: '0.95rem',
                                }}
                              >
                                {license.licenseState || '--'}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#6c757d',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                  fontSize: '0.7rem',
                                  mb: 0.5,
                                  display: 'block',
                                }}
                              >
                                Expiry Date
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 500,
                                  color: '#495057',
                                  fontSize: '0.95rem',
                                }}
                              >
                                {license.expiryDate
                                  ? formatDateMMDDYY(license.expiryDate)
                                  : '--'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#6c757d',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                  fontSize: '0.7rem',
                                  mb: 0.5,
                                  display: 'block',
                                }}
                              >
                                License Status
                              </Typography>
                              <Chip
                                label={license.licenseStatus || '--'}
                                color={
                                  license.licenseStatus === 'Active'
                                    ? 'success'
                                    : 'default'
                                }
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  height: 28,
                                  '& .MuiChip-label': {
                                    px: 1.5,
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#6c757d',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                  fontSize: '0.7rem',
                                  mb: 0.5,
                                  display: 'block',
                                }}
                              >
                                License Document
                              </Typography>
                              {license.licenseFileUrl ? (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  href={license.licenseFileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  startIcon={<VisibilityIcon />}
                                  sx={{
                                    textTransform: 'none',
                                    mt: 0.5,
                                  }}
                                >
                                  View License Document
                                </Button>
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#6c757d',
                                    fontStyle: 'italic',
                                    mt: 0.5,
                                  }}
                                >
                                  No document available
                                </Typography>
                              )}
                            </Grid>
                          </Grid>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Insurance Accepted */}
          {provider.insuranceAccepted &&
            provider.insuranceAccepted.length > 0 && (
              <Grid item xs={12}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'white',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 3,
                        pb: 2,
                        borderBottom: '2px solid',
                        borderColor: '#5BC0BE',
                      }}
                    >
                      <HealthAndSafetyIcon
                        sx={{ color: '#5BC0BE', fontSize: 28 }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: '#2c3e50',
                          letterSpacing: 0.3,
                        }}
                      >
                        Accepted Insurances
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                      {provider.insuranceAccepted.map(
                        (insurance: any, index: number) => (
                          <Chip
                            key={index}
                            label={insurance.name || '--'}
                            sx={{
                              bgcolor: '#e3f2fd',
                              color: '#1976d2',
                              fontWeight: 600,
                              border: '1px solid #90caf9',
                              '&:hover': {
                                bgcolor: '#bbdefb',
                              },
                            }}
                            size="medium"
                          />
                        )
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

          {/* Additional Information */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'white',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 3,
                    pb: 2,
                    borderBottom: '2px solid',
                    borderColor: '#5BC0BE',
                  }}
                >
                  <InfoIcon sx={{ color: '#5BC0BE', fontSize: 28 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2c3e50',
                      letterSpacing: 0.3,
                    }}
                  >
                    Additional Information
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Bank Connected
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      {provider.isConnectBank ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleOpenBankDialog}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            borderRadius: '50px',
                            px: 1.5,
                            py: 0.25,
                            minHeight: 28,
                            bgcolor: '#e6f7f7',
                            color: '#3a9e9c',
                            borderColor: '#b2e5e4',
                            '&:hover': {
                              bgcolor: '#d0f0ef',
                              borderColor: '#3a9e9c',
                            },
                          }}
                        >
                          Yes <span style={{ fontWeight: 400, fontSize: '0.65rem', marginLeft: '4px' }}>(View)</span>
                        </Button>
                      ) : (
                        <Chip
                          label="No"
                          color="default"
                          size="small"
                          sx={{
                            fontWeight: 600,
                            height: 28,
                            '& .MuiChip-label': {
                              px: 1.5,
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Subscription Taken
                    </Typography>
                    <Chip
                      label={provider.isSubscriptionTaken ? 'Yes' : 'No'}
                      color={
                        provider.isSubscriptionTaken ? 'success' : 'default'
                      }
                      size="small"
                      sx={{
                        fontWeight: 600,
                        height: 28,
                        '& .MuiChip-label': {
                          px: 1.5,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Service Created
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      {provider.isServiceCreated ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleOpenServicesDialog}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            borderRadius: '50px',
                            px: 1.5,
                            py: 0.25,
                            minHeight: 28,
                            bgcolor: '#e6f7f7',
                            color: '#3a9e9c',
                            borderColor: '#b2e5e4',
                            '&:hover': {
                              bgcolor: '#d0f0ef',
                              borderColor: '#3a9e9c',
                            },
                          }}
                        >
                          Yes <span style={{ fontWeight: 400, fontSize: '0.65rem', marginLeft: '4px' }}>(View)</span>
                        </Button>
                      ) : (
                        <Chip
                          label="No"
                          color="default"
                          size="small"
                          sx={{
                            fontWeight: 600,
                            height: 28,
                            '& .MuiChip-label': {
                              px: 1.5,
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Grid>
                  {/* <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Slot Addedsss
                    </Typography>
                    <Chip
                      label={provider.isSlotAdded ? 'Yes' : 'No'}
                      color={provider.isSlotAdded ? 'success' : 'default'}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        height: 28,
                        '& .MuiChip-label': {
                          px: 1.5,
                        },
                      }}
                    />
                  </Grid> */}
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Accepts New Patients
                    </Typography>
                    <Chip
                      label={provider.isAcceptNewPatientEnq ? 'Yes' : 'No'}
                      color={
                        provider.isAcceptNewPatientEnq ? 'success' : 'default'
                      }
                      size="small"
                      sx={{
                        fontWeight: 600,
                        height: 28,
                        '& .MuiChip-label': {
                          px: 1.5,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      HIPAA Signed
                    </Typography>
                    <Chip
                      label={provider.hasSignedHIPAA ? 'Yes' : 'No'}
                      color={provider.hasSignedHIPAA ? 'success' : 'default'}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        height: 28,
                        '& .MuiChip-label': {
                          px: 1.5,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Onboarding Date
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: '#2c3e50',
                        fontSize: '0.95rem',
                      }}
                    >
                      {formatDateMMDDYY(provider.createdAt) || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Last Updated
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: '#2c3e50',
                        fontSize: '0.95rem',
                      }}
                    >
                      {formatDateMMDDYY(provider.updatedAt) || '--'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      {actions && (
        <>
          <Divider sx={{ borderColor: '#e9ecef' }} />
          <DialogActions
            sx={{
              px: 3,
              py: 2,
              bgcolor: '#f8f9fa',
              justifyContent: 'flex-end',
              gap: 1.5,
            }}
          >
            {actions}
          </DialogActions>
        </>
      )}

      {/* Services List Dialog */}
      <Dialog
        open={servicesDialogOpen}
        onClose={handleCloseServicesDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '80vh',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
            pt: 3,
            px: 3,
            background: 'linear-gradient(135deg, #5BC0BE 0%, #4a9d9b 100%)',
            color: 'white',
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" fontWeight={700}>
              Services Created by {provider.agencyName || 'Agency'}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseServicesDialog} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, bgcolor: '#f8f9fa' }}>
          {loadingServices ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
              <Typography variant="body1" color="text.secondary">
                Loading services...
              </Typography>
            </Box>
          ) : services.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
              <Typography variant="body1" color="text.secondary" fontWeight={500}>
                No services found for this agency.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {services.map((service: any, index: number) => {
                const createdDateStr = service.createdAt ? dayjs(service.createdAt).format('MMM D, YYYY • h:mm A') : '--';
                const updatedDateStr = service.updatedAt ? dayjs(service.updatedAt).format('MMM D, YYYY • h:mm A') : '--';
                const modalities = Array.isArray(service.modality)
                  ? service.modality.map((m: string) => m === 'in_person' ? 'In-Person' : m).join(', ')
                  : 'In-Person';

                return (
                  <Grid item xs={12} key={service._id || index}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: '1px solid #e9ecef',
                        bgcolor: 'white',
                        p: 3,
                        mb: 2,
                        position: 'relative',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      {/* Top Row: Title + Status and Right side Created by */}
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} flexWrap="wrap" gap={1}>
                        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                          <Typography variant="h6" fontWeight={700} color="#2c3e50" sx={{ fontSize: '1.2rem' }}>
                            {service.name || '--'}
                          </Typography>
                          <Chip
                            label={(service.status || 'PUBLISHED').toUpperCase()}
                            size="small"
                            color="success"
                            sx={{
                              bgcolor: '#e6f7f0',
                              color: '#2e7d32',
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              height: 24,
                            }}
                          />
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            Created by : {service.created_by || 'Provider'}
                          </Typography>
                          <InfoIcon sx={{ fontSize: 16, color: '#0288d1' }} />
                        </Box>
                      </Box>

                      {/* Description */}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                        {service.short_description || service.long_description || 'No description available.'}
                      </Typography>

                      {/* Three columns of Stat Cards */}
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        {/* Rate */}
                        <Grid item xs={12} sm={4}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              bgcolor: '#f5f3ff',
                              border: '1px solid #e0dbff',
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                              <AttachMoneyIcon sx={{ fontSize: 16, color: '#7c4dff' }} />
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                Rate
                              </Typography>
                            </Box>
                            <Typography variant="h6" fontWeight={700} color="#7c4dff">
                              ${service.private_pay_rate || '0'}.00
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              per session
                            </Typography>
                          </Box>
                        </Grid>

                        {/* Duration */}
                        <Grid item xs={12} sm={4}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              bgcolor: '#faf5ff',
                              border: '1px solid #f3e8ff',
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                              <AccessTimeIcon sx={{ fontSize: 16, color: '#a855f7' }} />
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                Duration
                              </Typography>
                            </Box>
                            <Typography variant="h6" fontWeight={700} color="#a855f7">
                              {service.session_length || '0'} mins
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Typical session length
                            </Typography>
                          </Box>
                        </Grid>

                        {/* Search Visibility */}
                        <Grid item xs={12} sm={4}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              bgcolor: '#f0fdf4',
                              border: '1px solid #dcfce7',
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                              <VisibilityIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                Search Visibility
                              </Typography>
                            </Box>
                            <Typography variant="h6" fontWeight={700} color="#16a34a" sx={{ fontSize: '1rem' }}>
                              Visible to patients
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                              Patients can discover this package in search.
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Details Row: Specialization, Modalities, Timestamps */}
                      <Box
                        sx={{
                          p: 2.5,
                          borderRadius: 3,
                          bgcolor: '#f8fafc',
                          border: '1px solid #e2e8f0',
                        }}
                      >
                        <Grid container spacing={2}>
                          {/* Specialization */}
                          <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>
                              Specialization
                            </Typography>
                            <Typography variant="body2" fontWeight={600} color="#334155">
                              {service.specialization?.name || 'General'}
                            </Typography>
                          </Grid>

                          {/* Modalities */}
                          <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>
                              Modalities Offered
                            </Typography>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <PersonIcon sx={{ fontSize: 16, color: '#64748b' }} />
                              <Typography variant="body2" fontWeight={600} color="#64748b">
                                {modalities}
                              </Typography>
                            </Box>
                          </Grid>

                          {/* Dates */}
                          <Grid item xs={12} sm={4}>
                            <Box display="flex" flexDirection="column" gap={0.5}>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <CalendarTodayIcon sx={{ fontSize: 14, color: '#64748b' }} />
                                <Typography variant="caption" color="text.secondary">
                                  Created {createdDateStr}
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <AccessTimeIcon sx={{ fontSize: 14, color: '#64748b' }} />
                                <Typography variant="caption" color="text.secondary">
                                  Updated {updatedDateStr}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Card>
                  </Grid>
                )
              })}
            </Grid>
          )}
          {totalServices > servicesLimit && (
            <Box display="flex" justifyContent="center" sx={{ mt: 3 }}>
              <Pagination
                count={Math.ceil(totalServices / servicesLimit)}
                page={servicesPage}
                onChange={handleServicesPageChange}
                color="primary"
                size="medium"
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontWeight: 600,
                  }
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8f9fa' }}>
          <Button onClick={handleCloseServicesDialog} variant="outlined" sx={{ borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bank Details Dialog */}
      <Dialog
        open={bankDialogOpen}
        onClose={handleCloseBankDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
            pt: 3,
            px: 3,
            background: 'linear-gradient(135deg, #5BC0BE 0%, #4a9d9b 100%)',
            color: 'white',
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Bank Account Details
          </Typography>
          <IconButton onClick={handleCloseBankDialog} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4, bgcolor: '#f8fafc', mt: 2 }}>
          {loadingBank ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={6}>
              <CircularProgress size={32} sx={{ color: '#3a9e9c' }} />
            </Box>
          ) : !bankDetails ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <Typography variant="body2" color="text.secondary">
                No bank account details found.
              </Typography>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={3}>
              {/* Premium Bank Card Mock */}
              <Card
                elevation={0}
                sx={{
                  background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                  color: 'white',
                  borderRadius: 4,
                  p: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: '0 10px 25px rgba(15, 23, 42, 0.15)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                }}
              >
                {/* Background glow effects */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-50%',
                    right: '-20%',
                    width: '200px',
                    height: '200px',
                    background: 'radial-gradient(circle, rgba(91, 192, 190, 0.15) 0%, rgba(91, 192, 190, 0) 70%)',
                    borderRadius: '50%',
                    pointerEvents: 'none',
                  }}
                />

                {/* Bank Name + Icon */}
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 1 }}>
                      Connected Bank
                    </Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: 0.5, mt: 0.5 }}>
                      {bankDetails.bankName || 'STRIPE TEST BANK'}
                    </Typography>
                  </Box>
                  <AccountBalanceIcon sx={{ fontSize: 36, color: 'rgba(255, 255, 255, 0.3)' }} />
                </Box>

                {/* Account Number masking */}
                <Box mb={4}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 1 }}>
                    Account Number
                  </Typography>
                  <Typography variant="h5" fontWeight={600} sx={{ fontFamily: 'monospace', letterSpacing: 3, mt: 0.5 }}>
                    ••••  ••••  ••••  {bankDetails.last4 || '0000'}
                  </Typography>
                </Box>

                {/* Bottom Row inside card */}
                <Box display="flex" justifyContent="space-between" alignItems="flex-end">
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 1 }}>
                      Routing Number
                    </Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ letterSpacing: 1, mt: 0.5 }}>
                      {bankDetails.routingNumber || '---------'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 1, display: 'block', textAlign: 'right' }}>
                      Currency
                    </Typography>
                    <Typography variant="body1" fontWeight={700} sx={{ textTransform: 'uppercase', mt: 0.5, color: '#5BC0BE' }}>
                      {bankDetails.currency || 'USD'}
                    </Typography>
                  </Box>
                </Box>
              </Card>

              {/* Extra Metadata Details */}
              <Box sx={{ p: 2.5, borderRadius: 3, bgcolor: 'white', border: '1px solid #e2e8f0' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>
                      Country
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="#334155">
                      {bankDetails.country || 'US'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>
                      Verification Status
                    </Typography>
                    <Chip
                      label={(bankDetails.status || 'unknown').toUpperCase()}
                      size="small"
                      color={bankDetails.status === 'verified' ? 'success' : 'warning'}
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 24,
                        bgcolor: bankDetails.status === 'verified' ? '#e6f7f0' : '#fff3e0',
                        color: bankDetails.status === 'verified' ? '#2e7d32' : '#e65100',
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8f9fa' }}>
          <Button onClick={handleCloseBankDialog} variant="outlined" sx={{ borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

export default AgencyDetailsDialog
